// Returns the minimum number of single-character edits (insertions, deletions,
// substitutions) required to transform string `a` into string `b`.
//
// Iteration uses Unicode code points (`Array.from`) so a single surrogate-pair emoji
// counts as one character (UTF-16 `String` indexing does not).
//
// Used as the raw edit-distance input to `retainedRatio`. A distance of 0 means
// the strings are identical; larger values indicate more divergence. Reported as
// `editDistance` in the `DraftAssist: Suggestion Edited After Accept` Amplitude
// event so we can quantify how much users rewrite accepted suggestions before
// submitting recognition.
//
// Uses an O(n) space rolling-array DP to avoid allocating an m×n matrix.
export function levenshteinDistance(a: string, b: string): number {
	const aChars = Array.from(a);
	const bChars = Array.from(b);
	const m = aChars.length,
		n = bChars.length;
	let prev = Array.from({ length: n + 1 }, (_, i) => i);
	let curr = new Array(n + 1).fill(0);
	for (let i = 1; i <= m; i++) {
		curr[0] = i;
		for (let j = 1; j <= n; j++) {
			curr[j] = aChars[i - 1] === bChars[j - 1] ? prev[j - 1] : 1 + Math.min(prev[j - 1], prev[j], curr[j - 1]);
		}
		[prev, curr] = [curr, prev];
	}
	return prev[n];
}

/** When retained ratio drops at or below this, treat the suggestion as effectively deleted (`deletedEntirely` in Draft Assist analytics). */
export const DELETED_ENTIRELY_RETAINED_RATIO_THRESHOLD = 0.1;

// Returns a [0, 1] score representing how much of the accepted Draft Assist
// suggestion survived in the final submitted text.
//
// 1.0 — submitted text is identical to the accepted suggestion (nothing changed).
// 0.0 — the two strings share no characters at all (fully rewritten).
//
// Reported as `retainedRatio` in the `DraftAssist: Suggestion Edited After Accept`
// Amplitude event. The companion `deletedEntirely` flag is set when this ratio
// drops at or below `DELETED_ENTIRELY_RETAINED_RATIO_THRESHOLD`, indicating the user
// replaced the suggestion almost completely.
// Together these two fields let us distinguish light polishing from full rewrites
// in the Draft Assist funnel.
export function retainedRatio(accepted: string, submitted: string, distance = levenshteinDistance(accepted, submitted)): number {
	const maxLen = Math.max(Array.from(accepted).length, Array.from(submitted).length);
	if (maxLen === 0) return 1;
	return Math.max(0, 1 - distance / maxLen);
}
